import type {FC, MouseEvent} from 'react';
import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import moment from 'moment';
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@wellstone-solutions/web';
import {recoveryDatesQueryOptions} from 'queries';
import {headerPalette} from '../MemberHeaderCard/palette';
import {RecoveryHistoryPopover} from './RecoveryHistoryPopover';
import {RecoveryLeafIcon} from './RecoveryLeafIcon';
import {humanizeRecoveryDuration} from './utils';

interface RecoveryCounterProps {
  // Auth-user id (member.user.id), not the member id
  userId: string;
  memberName?: string;
}

// Recovery dates are midnight UTC — format in UTC so the day doesn't shift
const formatDayUTC = (value: string) =>
  moment.utc(value).format('ddd MMM D, YYYY');

export const RecoveryCounter: FC<RecoveryCounterProps> = ({
  userId,
  memberName,
}) => {
  const [historyAnchor, setHistoryAnchor] = useState<HTMLElement | null>(null);

  const currentQuery = useQuery({
    ...recoveryDatesQueryOptions.currentForUser(userId),
    enabled: !!userId,
  });

  // Nothing while loading/erroring; an explicit empty state once we know
  if (currentQuery.data === undefined) {
    return null;
  }

  const current = currentQuery.data;
  const firstName = memberName?.split(' ')[0] ?? 'This member';

  if (current === null) {
    return (
      <Box
        sx={{...styles.card, ...styles.cardEmpty}}
        data-testid="recovery-counter-card">
        <Stack direction="row" alignItems="center" spacing={1}>
          <RecoveryLeafIcon color={headerPalette.textSecondary} />
          <Typography
            sx={{...styles.eyebrow, color: headerPalette.textSecondary}}>
            Recovery Counter
          </Typography>
        </Stack>
        <Typography
          sx={{...styles.duration, color: headerPalette.textDisabled}}>
          Not set in Companion yet
        </Typography>
        <Typography sx={styles.subtitle}>
          {firstName} hasn't started a counter in the Companion app.
        </Typography>
      </Box>
    );
  }

  const hasReset = current.previousRecoveryDate !== null;

  return (
    <>
      <Box sx={styles.card} data-testid="recovery-counter-card">
        <Stack direction="row" alignItems="center" spacing={1}>
          <RecoveryLeafIcon color={headerPalette.violet} />
          <Typography sx={styles.eyebrow}>Recovery Counter</Typography>
          <Tooltip title="View history">
            {/* Native span: web's components don't forward refs, which
                prevents the Tooltip from anchoring and opening */}
            <span style={{display: 'inline-flex'}}>
              <IconButton
                icon="Calendar"
                iconProps={{
                  name: 'Calendar',
                  size: 18,
                  color: '#FFFFFF',
                }}
                size="small"
                sx={styles.historyButton}
                aria-label="Recovery Counter history"
                data-testid="recovery-counter-history-button"
                onClick={(event: MouseEvent<HTMLButtonElement>) =>
                  setHistoryAnchor(event.currentTarget)
                }
              />
            </span>
          </Tooltip>
        </Stack>

        <Typography
          sx={styles.duration}
          data-testid="recovery-counter-duration">
          {humanizeRecoveryDuration(current.recoveryDate)}
        </Typography>
        <Typography sx={styles.subtitle}>
          {hasReset ? 'Reset' : 'Set'} {formatDayUTC(current.recoveryDate)}
        </Typography>
      </Box>

      {historyAnchor && (
        <RecoveryHistoryPopover
          userId={userId}
          memberName={memberName}
          anchorEl={historyAnchor}
          onClose={() => setHistoryAnchor(null)}
        />
      )}
    </>
  );
};

const styles = {
  card: {
    backgroundColor: headerPalette.whisper,
    border: `1px solid ${headerPalette.violetSoft}`,
    borderRadius: 2,
    marginTop: 1.5,
    paddingX: '19px',
    paddingY: '17px',
    width: '100%',
  },
  cardEmpty: {
    backgroundColor: '#FAFAFA',
    border: `1px solid ${headerPalette.divider}`,
  },
  // Filled round button ("Button: filled, round" option from ENG-5368)
  // so the history affordance reads as a button instead of a bare glyph
  historyButton: {
    backgroundColor: headerPalette.violet,
    '&:hover': {
      backgroundColor: headerPalette.violetDark,
    },
  },
  eyebrow: {
    color: headerPalette.violet,
    flex: 1,
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  duration: {
    color: headerPalette.violet,
    fontSize: 24,
    fontWeight: 700,
    lineHeight: '30px',
    paddingTop: '6px',
  },
  subtitle: {
    color: headerPalette.textSecondary,
    fontSize: 13,
    marginTop: '2px',
  },
};
